import React, { useRef, useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { sendPlayerState, subscribeToPlayerState } from '../../../../services/gameService';
import { useAppContext } from '../../../../AppContext';
import { playerIconSvg } from './icons/playerIcon';
import { phantomIconSvg, reaperIconSvg } from './icons/enemyIcons';
import { beamProjectileIconSvg } from './icons/projectileIcons';
import { useTranslation } from 'react-i18next';

// --- HOOK for loading SVG strings as images ---
const useImage = (svgString) => {
    const [image, setImage] = useState(null);
    useEffect(() => {
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.src = url;
        img.onload = () => setImage(img);
        return () => URL.revokeObjectURL(url);
    }, [svgString]);
    return image;
};

// --- ENUMS (as objects) ---
const GameState = { MainMenu: 'MainMenu', Playing: 'Playing', Paused: 'Paused', LevelUp: 'LevelUp', GameOver: 'GameOver' };
const EnemyType = { Phantom: 'Phantom', Reaper: 'Reaper', Leech: 'Leech', Hunter: 'Hunter', Carrier: 'Carrier', Mite: 'Mite', Boss: 'Boss' };
const UpgradeType = { Weapon: 'Weapon', WeaponEvolution: 'WeaponEvolution', Legendary: 'Legendary', Stat: 'Stat' };

// --- CONSTANTS (Overhauled for faster gameplay) ---
const GAME_WIDTH = 1280;
const GAME_HEIGHT = 720;
const PLAYER_SPEED = 5;
const PLAYER_RADIUS = 15;
const PLAYER_INITIAL_HEALTH = 100;
const PLAYER_MAX_OVERDRIVE = 100;
const OVERDRIVE_DURATION = 5000;
const BASE_XP_TO_NEXT_LEVEL = 20;
const XP_MULTIPLIER_EARLY = 1.25;
const XP_MULTIPLIER_LATE = 1.8;
const COMBO_TIMEOUT = 3000;
const STYLE_THRESHOLDS = { D: 0, C: 10, B: 25, A: 50, S: 100, SS: 150 };
const ENEMY_SPAWN_RATE_INITIAL = 600; // Start spawning at ~2 per second
const ENEMY_SPAWN_RATE_MIN = 200;   // Accelerate to a max of ~4 per second
const ENEMY_SPAWN_ACCELERATION = 0.995; // Accelerate slightly faster
const ENEMY_HEALTH_SCALE_PER_SECOND = 0.013; // Enemies get tougher slower
const ENEMY_STATS = {
    Phantom: { radius: 12, health: 10, speed: 3.8, xp: 10, damage: 10 },
    Reaper: { radius: 18, health: 80, speed: 2.0, xp: 30, damage: 20, fireRate: 3000 },
    Hunter: { radius: 14, health: 40, speed: 5.8, xp: 25, damage: 15 },
    Carrier: { radius: 30, health: 200, speed: 1.2, xp: 50, damage: 10 },
    Mite: { radius: 6, health: 5, speed: 5.0, xp: 2, damage: 5 },
    Boss: { radius: 80, health: 20000, speed: 2.2, xp: 1000, damage: 50 }
};
const BOSS_SPAWN_TIME = 90 * 1000; // Boss spawns at 90 seconds
const AI_THINK_INTERVAL = 150;

// --- UTILITY FUNCTIONS ---
const dist = (p1, p2) => Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
const getRandom = (min, max) => Math.random() * (max - min) + min;
let nextId = 0;
const getNextId = () => nextId++;

const StarlightSentinel = ({ dreamWeaveId, nexusId, souls = 0, isSpectatorMode = false, onGameSubmit, onGameOver, isRemoteMode = false, syncId, myRole, remoteRole, autoStart = false }) => {
    const { t } = useTranslation();
    const { appFunctions } = useAppContext();
    const canvasRef = useRef(null);
    const gameLoopId = useRef(0);

    // --- Load Images ---
    const playerImage = useImage(playerIconSvg);
    const phantomImage = useImage(phantomIconSvg);
    const reaperImage = useImage(reaperIconSvg);
    const beamProjectileImage = useImage(beamProjectileIconSvg);
    
    const [gameState, setGameState] = useState(GameState.MainMenu);
    const [score, setScore] = useState(0);
    const scoreRef = useRef(score);
    useEffect(() => {
        scoreRef.current = score;
    }, [score]);
    const [gameTime, setGameTime] = useState(0);
    const [combo, setCombo] = useState(0);
    const [styleRank, setStyleRank] = useState('D');
    const [upgradeChoices, setUpgradeChoices] = useState([]);
    const [isAiThinking, setIsAiThinking] = useState(false);
    const [nextBossTime, setNextBossTime] = useState(BOSS_SPAWN_TIME); // Kept for UI display
    const nextBossTimeRef = useRef(BOSS_SPAWN_TIME); // Used for game logic

    const playerRef = useRef(null);
    const enemiesRef = useRef([]);
    const projectilesRef = useRef([]);
    const particlesRef = useRef([]);
    const explosionsRef = useRef([]);
    const vortexesRef = useRef([]);
    const dronesRef = useRef([]);
    const beamsRef = useRef([]);
    const timeFieldsRef = useRef([]);
    const xpGemsRef = useRef([]);
    const comboTimeoutRef = useRef(0);
    const enemySpawnTimerRef = useRef(0);
    const currentSpawnRateRef = useRef(ENEMY_SPAWN_RATE_INITIAL);
    const lastTimeRef = useRef(0);
    const screenShakeRef = useRef({ magnitude: 0, duration: 0 });
    const lastAiActionTimestamp = useRef(0);
    const lastSyncTime = useRef(0);

    // --- SYNC (Remote Mode) ---
    useEffect(() => {
        if (isRemoteMode && syncId && remoteRole) {
            const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
                if (data && playerRef.current) {
                    playerRef.current.position = data.pos;
                    playerRef.current.rotation = data.rot;
                    playerRef.current.isOverdriveActive = data.overdrive;
                    
                    if (gameState === GameState.MainMenu || gameState === GameState.GameOver) {
                        startGame(); 
                    }
                } else if (data && !playerRef.current) {
                    startGame();
                }
            });
            return () => unsubscribe();
        }
    }, [isRemoteMode, syncId, remoteRole, gameState]);

    useEffect(() => {
        if ((isSpectatorMode || isRemoteMode || autoStart) && gameState === GameState.MainMenu) {
            // Wait for sync or auto-start if pure AI spectator OR autoStart is requested
            if (!isRemoteMode) startGame();
        }
    }, [isSpectatorMode, isRemoteMode, gameState, autoStart]);

    // --- ENTITY CREATION FUNCTIONS ---
    const createBeamProjectile = (player, angle, level) => ({ id: getNextId(), position: { ...player.position }, velocity: { x: Math.cos(angle) * 10, y: Math.sin(angle) * 10 }, radius: 4 + level, health: 1, maxHealth: 1, isAlive: true, isPlayerProjectile: true, damage: 10 + level * 5, lifespan: 2000, render: (ctx, proj) => { 
        if (beamProjectileImage) {
            ctx.save();
            ctx.translate(proj.position.x, proj.position.y);
            ctx.rotate(Math.atan2(proj.velocity.y, proj.velocity.x) + Math.PI / 2);
            ctx.drawImage(beamProjectileImage, -proj.radius, -proj.radius * 2, proj.radius * 2, proj.radius * 4);
            ctx.restore();
        } else {
            ctx.fillStyle = player.isOverdriveActive ? '#FF00FF' : '#00FFFF'; ctx.shadowColor = player.isOverdriveActive ? '#FF00FF' : '#00FFFF'; ctx.shadowBlur = 15; ctx.beginPath(); ctx.arc(proj.position.x, proj.position.y, proj.radius, 0, Math.PI * 2); ctx.fill();
        }
    } });
    const createNovaProjectile = (player, level, radiusMultiplier = 1) => { const radius = (50 + level * 20) * radiusMultiplier; return { id: getNextId(), position: { ...player.position }, velocity: { x: 0, y: 0 }, radius: 0, health: 999, maxHealth: 999, isAlive: true, isPlayerProjectile: true, damage: 20 + level * 10, lifespan: 300, alreadyHit: [], render: (ctx, proj) => { const lifetimeProgress = 1 - (proj.lifespan / 300); const currentRadius = radius * lifetimeProgress; proj.radius = currentRadius; ctx.strokeStyle = player.isOverdriveActive ? '#FFFF00' : '#87CEFA'; ctx.lineWidth = 5; ctx.shadowColor = player.isOverdriveActive ? '#FFFF00' : '#87CEFA'; ctx.shadowBlur = 15; ctx.globalAlpha = Math.max(0, proj.lifespan / 300); ctx.beginPath(); ctx.arc(proj.position.x, proj.position.y, Math.max(0, currentRadius), 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1.0; } }; };
    const createMissileProjectile = (player, targetId, level, isEvolved) => { const angle = Math.random() * Math.PI * 2; return { id: getNextId(), position: { ...player.position }, velocity: { x: Math.cos(angle) * (isEvolved ? 8 : 6), y: Math.sin(angle) * (isEvolved ? 8 : 6) }, radius: isEvolved ? 5 : 7, health: 1, maxHealth: 1, isAlive: true, isPlayerProjectile: true, damage: 15 + level * 5, lifespan: 5000, targetId: targetId, turnRate: 0.1, render: (ctx, proj) => { ctx.fillStyle = isEvolved ? '#FF5733' : '#FFC300'; ctx.shadowColor = isEvolved ? '#FF5733' : '#FFC300'; ctx.shadowBlur = 10; ctx.beginPath(); ctx.arc(proj.position.x, proj.position.y, proj.radius, 0, Math.PI * 2); ctx.fill(); } }; };
    const createDroneProjectile = (drone, angle, level) => ({ id: getNextId(), position: { ...drone.position }, velocity: { x: Math.cos(angle) * 8, y: Math.sin(angle) * 8 }, radius: 3 + level * 0.5, health: 1, maxHealth: 1, isAlive: true, isPlayerProjectile: true, damage: 8 + level * 3, lifespan: 1000, render: (ctx, proj) => { ctx.fillStyle = '#90ee90'; ctx.shadowColor = '#90ee90'; ctx.shadowBlur = 10; ctx.beginPath(); ctx.arc(proj.position.x, proj.position.y, proj.radius, 0, Math.PI * 2); ctx.fill(); } });
    const createRipperProjectile = (player, angle, level) => ({ id: getNextId(), position: { ...player.position }, velocity: { x: Math.cos(angle) * 15, y: Math.sin(angle) * 15 }, radius: 5, health: 1 + level, maxHealth: 1 + level, isAlive: true, isPlayerProjectile: true, damage: 8 + level * 2, lifespan: 200, alreadyHit: [], render: (ctx, proj) => { ctx.fillStyle = '#FFFFFF'; ctx.shadowColor = '#FFFFFF'; ctx.shadowBlur = 10; ctx.beginPath(); ctx.rect(proj.position.x - 10, proj.position.y - 2, 20, 4); ctx.fill(); } });
    const createChronoProjectile = (player, angle, level) => ({ id: getNextId(), position: { ...player.position }, velocity: { x: Math.cos(angle) * 12, y: Math.sin(angle) * 12 }, radius: 4, health: 1, maxHealth: 1, isAlive: true, isPlayerProjectile: true, damage: 5 + level, lifespan: 1500, onHit: (target) => { target.speedModifier = 0.5; target.speedModifierDuration = 1000 + level * 200; }, render: (ctx, proj) => { ctx.fillStyle = '#00BFFF'; ctx.shadowColor = '#00BFFF'; ctx.shadowBlur = 10; ctx.beginPath(); ctx.arc(proj.position.x, proj.position.y, proj.radius, 0, Math.PI * 2); ctx.fill(); } });
    const createWarpSlashProjectile = (player, angle, level, isEvolved) => ({
        id: getNextId(),
        position: { ...player.position },
        velocity: { x: 0, y: 0 }, // The slash is instantaneous and doesn't move
        radius: 80 + level * 10, // The arc radius of the slash
        health: 999, maxHealth: 999, isAlive: true, isPlayerProjectile: true,
        damage: 30 + level * 10,
        lifespan: 150, // Short lifespan for the visual effect
        alreadyHit: [],
        render: (ctx, proj) => {
            const lifetimeProgress = 1 - (proj.lifespan / 150);
            const currentAngle = Math.PI * 1.5 * lifetimeProgress;
            const startAngle = angle - currentAngle / 2;
            const endAngle = angle + currentAngle / 2;

            ctx.strokeStyle = isEvolved ? '#FF00FF' : '#FFFFFF';
            ctx.lineWidth = 10;
            ctx.shadowColor = isEvolved ? '#FF00FF' : '#FFFFFF';
            ctx.shadowBlur = 20;
            ctx.globalAlpha = Math.max(0, proj.lifespan / 150);
            ctx.beginPath();
            ctx.arc(proj.position.x, proj.position.y, proj.radius, startAngle, endAngle);
            ctx.stroke();
            ctx.globalAlpha = 1.0;
        }
    });
    const createScytheProjectile = (enemy, angle) => ({
        id: getNextId(),
        position: { ...enemy.position },
        velocity: { x: Math.cos(angle) * 4, y: Math.sin(angle) * 4 },
        radius: 15,
        health: 3, // Can pierce 2 enemies
        maxHealth: 3,
        isAlive: true,
        isPlayerProjectile: false,
        damage: 20,
        lifespan: 3000,
        render: (ctx, proj) => {
            ctx.save();
            ctx.translate(proj.position.x, proj.position.y);
            ctx.rotate(gameTime * 0.01); // Spinning effect
            ctx.strokeStyle = '#FF00FF';
            ctx.lineWidth = 3;
            ctx.shadowColor = '#FF00FF';
            ctx.shadowBlur = 15;
            ctx.beginPath();
            ctx.arc(0, 0, proj.radius, 0, Math.PI * 1.5);
            ctx.stroke();
            ctx.restore();
        }
    });

    const createBossProjectile = (enemy, angle, speed = 6) => ({
        id: getNextId(),
        position: { ...enemy.position },
        velocity: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        radius: 12,
        health: 1,
        maxHealth: 1,
        isAlive: true,
        isPlayerProjectile: false,
        damage: 35,
        lifespan: 4000,
        render: (ctx, proj) => {
            ctx.save();
            ctx.translate(proj.position.x, proj.position.y);
            ctx.rotate(gameTime * 0.02); // Spinning effect
            ctx.strokeStyle = '#DDA0DD'; // Light purple
            ctx.fillStyle = '#8A2BE2'; // Blue-violet
            ctx.lineWidth = 3;
            ctx.shadowColor = '#DDA0DD';
            ctx.shadowBlur = 15;
            ctx.beginPath();
            // Simple diamond shape
            ctx.moveTo(0, -proj.radius);
            ctx.lineTo(proj.radius, 0);
            ctx.lineTo(0, proj.radius);
            ctx.lineTo(-proj.radius, 0);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.restore();
        }
    });

    // --- WEAPON & UPGRADE DEFINITIONS ---
    const manageDrones = (player) => {
        dronesRef.current = dronesRef.current.filter(d => d.ownerId !== player.id);
        const weapon = player.weapons.find(w => w.id === 'guardianDrones');
        if (!weapon) return;
        const droneCount = weapon.level === 1 ? 2 : weapon.level <= 3 ? 3 : 4;
        const isEvolved = weapon.isEvolved;
        for (let i = 0; i < droneCount; i++) {
            dronesRef.current.push({
                id: getNextId(), ownerId: player.id, position: { ...player.position }, velocity: { x: 0, y: 0 },
                radius: isEvolved ? 12 : 8, health: 999, maxHealth: 999, isAlive: true, orbitRadius: 75,
                orbitAngle: (i / droneCount) * Math.PI * 2, orbitSpeed: 2,
                fireCooldown: isEvolved ? 500 : 2000 - weapon.level * 200, fireTimer: Math.random() * 500,
                isEvolved: isEvolved,
            });
        }
    };

    const createCelestialBeam = () => {
        let beam = {
            id: 'celestialBeam', name: 'Celestial Beam', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 800, timer: 0,
            getUpgradeDescription: () => `Level ${beam.level + 1}: Increases fire rate.`,
            update: (player, enemies, projectiles) => {
                if (beam.timer <= 0 && enemies.length > 0) {
                    const target = enemies.reduce((prev, curr) => dist(player.position, prev.position) < dist(player.position, curr.position) ? prev : curr);
                    player.lastTargetPosition = { ...target.position };
                    if (beam.isEvolved) {
                        const angleToTarget = Math.atan2(target.position.y - player.position.y, target.position.x - player.position.x);
                        projectiles.push(createBeamProjectile(player, angleToTarget - 0.2, beam.level));
                        projectiles.push(createBeamProjectile(player, angleToTarget, beam.level));
                        projectiles.push(createBeamProjectile(player, angleToTarget + 0.2, beam.level));
                    } else {
                        projectiles.push(createBeamProjectile(player, Math.atan2(target.position.y - player.position.y, target.position.x - player.position.x), beam.level));
                    }
                    beam.timer = beam.cooldown / (player.isOverdriveActive ? 3 : 1);
                }
            }
        };
        return beam;
    };

    const createPulseNova = () => {
        let weapon = {
            id: 'pulseNova', name: 'Pulse Nova', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 4000, timer: 0,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: Shorter cooldown, larger radius.`,
            update: (player, enemies, projectiles) => {
                if (weapon.timer <= 0) {
                    projectiles.push(createNovaProjectile(player, weapon.level));
                    if (weapon.isEvolved) {
                        setTimeout(() => {
                            if (playerRef.current?.isAlive) {
                                projectiles.push(createNovaProjectile(player, weapon.level, 1.5));
                            }
                        }, 250);
                    }
                    weapon.timer = weapon.cooldown / (player.isOverdriveActive ? 2 : 1);
                }
            }
        };
        return weapon;
    };

    const createStarlightVortex = () => {
        let weapon = {
            id: 'starlightVortex', name: 'Starlight Vortex', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 8000, timer: 0,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: Longer duration, stronger pull.`,
            update: (player, enemies, projectiles, vortexes) => {
                if (weapon.timer <= 0 && enemies.length > 0) {
                    const targetEnemy = enemies[Math.floor(Math.random() * enemies.length)];
                    vortexes.push({
                        id: getNextId(), position: { ...targetEnemy.position }, velocity: { x: 0, y: 0 },
                        radius: 20 + weapon.level * 5, health: 1, maxHealth: 1, isAlive: true,
                        pullRadius: weapon.isEvolved ? 250 + weapon.level * 40 : 100 + weapon.level * 20,
                        pullStrength: weapon.isEvolved ? 0.5 : 0.2, damagePerSecond: 15 + weapon.level * 5,
                        lifespan: 3000 + weapon.level * 500, maxLifespan: 3000 + weapon.level * 500,
                    });
                    weapon.timer = weapon.cooldown / (player.isOverdriveActive ? 2 : 1);
                }
            }
        };
        return weapon;
    };

    const createGuardianDrones = () => ({
        id: 'guardianDrones', name: 'Guardian Drones', level: 1, maxLevel: 5, isEvolved: false,
        cooldown: 0, timer: 0,
        getUpgradeDescription: () => `Level ${this.level + 1}: Increases drone count or fire rate.`,
        update: () => { }
    });

    const createStarfireMissiles = () => {
        let weapon = {
            id: 'starfireMissiles', name: 'Starfire Missiles', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 3000, timer: 0,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: More missiles, shorter cooldown.`,
            update: (player, enemies, projectiles) => {
                if (weapon.timer <= 0 && enemies.length > 0) {
                    const missileCount = weapon.isEvolved ? 8 : 1 + Math.floor(weapon.level / 2);
                    const sortedEnemies = [...enemies].sort((a, b) => dist(player.position, a.position) - dist(player.position, b.position));
                    for (let i = 0; i < missileCount && i < sortedEnemies.length; i++) {
                        projectiles.push(createMissileProjectile(player, sortedEnemies[i].id, weapon.level, weapon.isEvolved));
                    }
                    weapon.timer = weapon.cooldown / (player.isOverdriveActive ? 2 : 1);
                }
            }
        };
        return weapon;
    };

    const createWarpStrike = () => {
        let weapon = {
            id: 'warpStrike', name: 'Warp Strike', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 2500, timer: 0,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: Faster cooldown, increased range.`,
            update: (player, enemies, projectiles) => {
                if (weapon.timer <= 0 && enemies.length > 0) {
                    const target = enemies.reduce((prev, curr) => dist(player.position, prev.position) < dist(player.position, curr.position) ? prev : curr);
                    const distanceToTarget = dist(player.position, target.position);
                    const warpRange = 150 + weapon.level * 20;

                    if (distanceToTarget < warpRange) {
                        const angleToTarget = Math.atan2(target.position.y - player.position.y, target.position.x - player.position.x);
                        
                        // Store original position and warp
                        const originalPosition = { ...player.position };
                        player.position.x = target.position.x - Math.cos(angleToTarget) * (target.radius + player.radius);
                        player.position.y = target.position.y - Math.sin(angleToTarget) * (target.radius + player.radius);

                        // Create slash effect at the new position
                        projectiles.push(createWarpSlashProjectile(player, angleToTarget, weapon.level, weapon.isEvolved));
                        
                        // Evolved effect: Void Edge explosion
                        if (weapon.isEvolved) {
                            createExplosion(player.position, 120, '#FF00FF');
                            enemies.forEach(e => {
                                if (dist(player.position, e.position) < 120) {
                                    e.health -= 50 + weapon.level * 10; // Explosion damage
                                }
                            });
                        }

                        // Return to original position after a brief moment
                        setTimeout(() => {
                            if (playerRef.current) {
                                playerRef.current.position = originalPosition;
                            }
                        }, 100);

                        weapon.timer = weapon.cooldown / (player.isOverdriveActive ? 2 : 1);
                    }
                }
            }
        };
        return weapon;
    };

    const createAegisShield = () => {
        let weapon = {
            id: 'aegisShield', name: 'Aegis Shield', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 0, timer: 0, // Shield is always active
            shieldHealth: 50,
            maxShieldHealth: 50,
            regenDelay: 5000,
            regenTimer: 5000,
            absorbedDamage: 0,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: Increases shield health and damage.`,
            update: (player, enemies, projectiles) => {
                // Shield regeneration
                if (weapon.shieldHealth < weapon.maxShieldHealth) {
                    weapon.regenTimer -= 16; // Approximate deltaTime
                    if (weapon.regenTimer <= 0) {
                        weapon.shieldHealth += 5; // Regen rate
                        if (weapon.shieldHealth > weapon.maxShieldHealth) {
                            weapon.shieldHealth = weapon.maxShieldHealth;
                        }
                    }
                }

                // Evolved ability: Bulwark Protocol
                if (weapon.isEvolved && weapon.absorbedDamage > 0) {
                    const target = enemies.reduce((prev, curr) => dist(player.position, prev.position) < dist(player.position, curr.position) ? prev : curr);
                    if (target) {
                         // Fires a projectile based on absorbed damage
                        const angleToTarget = Math.atan2(target.position.y - player.position.y, target.position.x - player.position.x);
                        projectiles.push({
                            id: getNextId(),
                            position: { ...player.position },
                            velocity: { x: Math.cos(angleToTarget) * 7, y: Math.sin(angleToTarget) * 7 },
                            radius: 5 + Math.min(weapon.absorbedDamage / 20, 15),
                            damage: weapon.absorbedDamage,
                            lifespan: 1000,
                            isPlayerProjectile: true,
                            isAlive: true,
                            render: (ctx, proj) => {
                                ctx.fillStyle = '#FFFF00';
                                ctx.shadowColor = '#FFFF00';
                                ctx.shadowBlur = 15;
                                ctx.beginPath();
                                ctx.arc(proj.position.x, proj.position.y, proj.radius, 0, Math.PI * 2);
                                ctx.fill();
                            }
                        });
                        weapon.absorbedDamage = 0; // Reset absorbed damage
                    }
                }
            }
        };
        return weapon;
    };

    const createStasisField = () => {
        let weapon = {
            id: 'stasisField', name: 'Stasis Field', level: 1, maxLevel: 5, isEvolved: false,
            cooldown: 0, timer: 0, // Always active
            radius: 150,
            slowFactor: 0.7,
            getUpgradeDescription: () => `Level ${weapon.level + 1}: Increases radius and slow effect.`,
            update: (player, enemies) => {
                enemies.forEach(enemy => {
                    if (dist(player.position, enemy.position) < weapon.radius + enemy.radius) {
                        if (!enemy.effects) enemy.effects = {};
                        enemy.effects.stasisSlow = weapon.slowFactor;

                        // Apply freeze effect
                        if (!enemy.effects.freezeBuildup) enemy.effects.freezeBuildup = 0;
                        enemy.effects.freezeBuildup += 1; // Buildup rate
                        if (enemy.effects.freezeBuildup >= 100) {
                            enemy.effects.frozen = true;
                            enemy.effects.freezeTimer = 2000; // Freeze duration
                        }
                    }
                });
            }
        };
        return weapon;
    };
    
    const createQuantumRipper = () => { /* ... implementation ... */ };
    const createChronoRepeater = () => { /* ... implementation ... */ };

    const ALL_UPGRADES = {
        getPulseNova: { id: 'getPulseNova', name: t('starlightSentinel.upgrades.getPulseNova.name'), description: t('starlightSentinel.upgrades.getPulseNova.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createPulseNova()) },
        getStarlightVortex: { id: 'getStarlightVortex', name: t('starlightSentinel.upgrades.getStarlightVortex.name'), description: t('starlightSentinel.upgrades.getStarlightVortex.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createStarlightVortex()) },
        getGuardianDrones: { id: 'getGuardianDrones', name: t('starlightSentinel.upgrades.getGuardianDrones.name'), description: t('starlightSentinel.upgrades.getGuardianDrones.description'), type: UpgradeType.Weapon, apply: (player) => { player.weapons.push(createGuardianDrones()); manageDrones(player); } },
        getStarfireMissiles: { id: 'getStarfireMissiles', name: t('starlightSentinel.upgrades.getStarfireMissiles.name'), description: t('starlightSentinel.upgrades.getStarfireMissiles.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createStarfireMissiles()) },
        getWarpStrike: { id: 'getWarpStrike', name: t('starlightSentinel.upgrades.getWarpStrike.name'), description: t('starlightSentinel.upgrades.getWarpStrike.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createWarpStrike()) },
        getAegisShield: { id: 'getAegisShield', name: t('starlightSentinel.upgrades.getAegisShield.name'), description: t('starlightSentinel.upgrades.getAegisShield.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createAegisShield()) },
        getStasisField: { id: 'getStasisField', name: t('starlightSentinel.upgrades.getStasisField.name'), description: t('starlightSentinel.upgrades.getStasisField.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createStasisField()) },
        getQuantumRipper: { id: 'getQuantumRipper', name: t('starlightSentinel.upgrades.getQuantumRipper.name'), description: t('starlightSentinel.upgrades.getQuantumRipper.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createQuantumRipper()) },
        getChronoRepeater: { id: 'getChronoRepeater', name: t('starlightSentinel.upgrades.getChronoRepeater.name'), description: t('starlightSentinel.upgrades.getChronoRepeater.description'), type: UpgradeType.Weapon, apply: (player) => player.weapons.push(createChronoRepeater()) },
        celestialBeam_upgrade: { id: 'celestialBeam_upgrade', name: t('starlightSentinel.upgrades.celestialBeam_upgrade.name'), description: t('starlightSentinel.upgrades.celestialBeam_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'celestialBeam'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.85; } } },
        pulseNova_upgrade: { id: 'pulseNova_upgrade', name: t('starlightSentinel.upgrades.pulseNova_upgrade.name'), description: t('starlightSentinel.upgrades.pulseNova_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'pulseNova'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.9; } } },
        starlightVortex_upgrade: { id: 'starlightVortex_upgrade', name: t('starlightSentinel.upgrades.starlightVortex_upgrade.name'), description: t('starlightSentinel.upgrades.starlightVortex_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'starlightVortex'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.95; } } },
        guardianDrones_upgrade: { id: 'guardianDrones_upgrade', name: t('starlightSentinel.upgrades.guardianDrones_upgrade.name'), description: t('starlightSentinel.upgrades.guardianDrones_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'guardianDrones'); if (w && w.level < w.maxLevel) { w.level++; manageDrones(player); } } },
        starfireMissiles_upgrade: { id: 'starfireMissiles_upgrade', name: t('starlightSentinel.upgrades.starfireMissiles_upgrade.name'), description: t('starlightSentinel.upgrades.starfireMissiles_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'starfireMissiles'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.9; } } },
        warpStrike_upgrade: { id: 'warpStrike_upgrade', name: t('starlightSentinel.upgrades.warpStrike_upgrade.name'), description: t('starlightSentinel.upgrades.warpStrike_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'warpStrike'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.9; } } },
        aegisShield_upgrade: { id: 'aegisShield_upgrade', name: t('starlightSentinel.upgrades.aegisShield_upgrade.name'), description: t('starlightSentinel.upgrades.aegisShield_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'aegisShield'); if (w && w.level < w.maxLevel) { w.level++; w.maxShieldHealth += 25; w.shieldHealth = w.maxShieldHealth; w.regenDelay *= 0.9; } } },
        stasisField_upgrade: { id: 'stasisField_upgrade', name: t('starlightSentinel.upgrades.stasisField_upgrade.name'), description: t('starlightSentinel.upgrades.stasisField_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'stasisField'); if (w && w.level < w.maxLevel) { w.level++; w.radius += 20; w.slowFactor *= 0.9; } } },
        quantumRipper_upgrade: { id: 'quantumRipper_upgrade', name: t('starlightSentinel.upgrades.quantumRipper_upgrade.name'), description: t('starlightSentinel.upgrades.quantumRipper_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'quantumRipper'); if (w && w.level < w.maxLevel) { w.level++; } } },
        chronoRepeater_upgrade: { id: 'chronoRepeater_upgrade', name: t('starlightSentinel.upgrades.chronoRepeater_upgrade.name'), description: t('starlightSentinel.upgrades.chronoRepeater_upgrade.description'), type: UpgradeType.Weapon, apply: (player) => { const w = player.weapons.find(w => w.id === 'chronoRepeater'); if (w && w.level < w.maxLevel) { w.level++; w.cooldown *= 0.85; } } },
        trinityBeam: { id: 'trinityBeam', name: t('starlightSentinel.upgrades.trinityBeam.name'), description: t('starlightSentinel.upgrades.trinityBeam.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'celestialBeam'); if (w) { w.isEvolved = true; w.name = 'Trinity Beam'; } } },
        resonatingBlast: { id: 'resonatingBlast', name: t('starlightSentinel.upgrades.resonatingBlast.name'), description: t('starlightSentinel.upgrades.resonatingBlast.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'pulseNova'); if (w) { w.isEvolved = true; w.name = 'Resonating Blast'; } } },
        singularity: { id: 'singularity', name: t('starlightSentinel.upgrades.singularity.name'), description: t('starlightSentinel.upgrades.singularity.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'starlightVortex'); if (w) { w.isEvolved = true; w.name = 'Singularity'; } } },
        aegisOrdinance: { id: 'aegisOrdinance', name: t('starlightSentinel.upgrades.aegisOrdinance.name'), description: t('starlightSentinel.upgrades.aegisOrdinance.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'guardianDrones'); if (w) { w.isEvolved = true; w.name = 'Aegis Ordinance'; manageDrones(player); } } },
        cometSwarm: { id: 'cometSwarm', name: t('starlightSentinel.upgrades.cometSwarm.name'), description: t('starlightSentinel.upgrades.cometSwarm.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'starfireMissiles'); if (w) { w.isEvolved = true; w.name = 'Comet Swarm'; } } },
        voidEdge: { id: 'voidEdge', name: t('starlightSentinel.upgrades.voidEdge.name'), description: t('starlightSentinel.upgrades.voidEdge.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'warpStrike'); if (w) { w.isEvolved = true; w.name = 'Void Edge'; } } },
        bulwarkProtocol: { id: 'bulwarkProtocol', name: t('starlightSentinel.upgrades.bulwarkProtocol.name'), description: t('starlightSentinel.upgrades.bulwarkProtocol.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'aegisShield'); if (w) { w.isEvolved = true; w.name = 'Bulwark Protocol'; } } },
        temporalSingularity: { id: 'temporalSingularity', name: t('starlightSentinel.upgrades.temporalSingularity.name'), description: t('starlightSentinel.upgrades.temporalSingularity.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'stasisField'); if (w) { w.isEvolved = true; w.name = 'Temporal Singularity'; } } },
        realityFracture: { id: 'realityFracture', name: t('starlightSentinel.upgrades.realityFracture.name'), description: t('starlightSentinel.upgrades.realityFracture.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'quantumRipper'); if (w) { w.isEvolved = true; w.name = 'Reality Fracture'; } } },
        temporalStorm: { id: 'temporalStorm', name: t('starlightSentinel.upgrades.temporalStorm.name'), description: t('starlightSentinel.upgrades.temporalStorm.description'), type: UpgradeType.WeaponEvolution, apply: (player) => { const w = player.weapons.find(w => w.id === 'chronoRepeater'); if (w) { w.isEvolved = true; w.name = 'Temporal Storm'; } } },
        prismaticHeart: { id: 'prismaticHeart', name: t('starlightSentinel.upgrades.prismaticHeart.name'), description: t('starlightSentinel.upgrades.prismaticHeart.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'celestialBeam'); if (w) w.isLegendary = true; } },
        eventHorizon: { id: 'eventHorizon', name: t('starlightSentinel.upgrades.eventHorizon.name'), description: t('starlightSentinel.upgrades.eventHorizon.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'starlightVortex'); if (w) w.isLegendary = true; } },
        meteorCataclysm: { id: 'meteorCataclysm', name: t('starlightSentinel.upgrades.meteorCataclysm.name'), description: t('starlightSentinel.upgrades.meteorCataclysm.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'starfireMissiles'); if (w) w.isLegendary = true; } },
        unblinkingSentinels: { id: 'unblinkingSentinels', name: t('starlightSentinel.upgrades.unblinkingSentinels.name'), description: t('starlightSentinel.upgrades.unblinkingSentinels.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'guardianDrones'); if (w) w.isLegendary = true; } },
        echoingCollapse: { id: 'echoingCollapse', name: t('starlightSentinel.upgrades.echoingCollapse.name'), description: t('starlightSentinel.upgrades.echoingCollapse.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'pulseNova'); if (w) w.isLegendary = true; } },
        riftWalker: { id: 'riftWalker', name: t('starlightSentinel.upgrades.riftWalker.name'), description: t('starlightSentinel.upgrades.riftWalker.description'), type: UpgradeType.Legendary, apply: (player) => { const w = player.weapons.find(w => w.id === 'warpStrike'); if (w) w.isLegendary = true; } },
    };

    const generateUpgradeChoices = () => {
        const player = playerRef.current;
        if (!player) return;
        const choices = [];
        const ownedWeaponIds = player.weapons.map(w => w.id);
        const evolvable = player.weapons.find(w => w.level === w.maxLevel && !w.isEvolved);
        if (evolvable) {
            if (evolvable.id === 'celestialBeam') choices.push(ALL_UPGRADES.trinityBeam);
            if (evolvable.id === 'pulseNova') choices.push(ALL_UPGRADES.resonatingBlast);
            if (evolvable.id === 'starlightVortex') choices.push(ALL_UPGRADES.singularity);
            if (evolvable.id === 'guardianDrones') choices.push(ALL_UPGRADES.aegisOrdinance);
            if (evolvable.id === 'starfireMissiles') choices.push(ALL_UPGRADES.cometSwarm);
            if (evolvable.id === 'warpStrike') choices.push(ALL_UPGRADES.voidEdge);
            if (evolvable.id === 'aegisShield') choices.push(ALL_UPGRADES.bulwarkProtocol);
            if (evolvable.id === 'stasisField') choices.push(ALL_UPGRADES.temporalSingularity);
        }
        const potentialUpgrades = player.weapons.filter(w => w.level < w.maxLevel).map(w => ALL_UPGRADES[`${w.id}_upgrade`]);
        choices.push(...potentialUpgrades);
        if (player.weapons.length < 6) {
            if (!ownedWeaponIds.includes('pulseNova')) choices.push(ALL_UPGRADES.getPulseNova);
            if (!ownedWeaponIds.includes('starlightVortex')) choices.push(ALL_UPGRADES.getStarlightVortex);
            if (!ownedWeaponIds.includes('guardianDrones')) choices.push(ALL_UPGRADES.getGuardianDrones);
            if (!ownedWeaponIds.includes('starfireMissiles')) choices.push(ALL_UPGRADES.getStarfireMissiles);
            if (!ownedWeaponIds.includes('warpStrike')) choices.push(ALL_UPGRADES.getWarpStrike);
            if (!ownedWeaponIds.includes('aegisShield')) choices.push(ALL_UPGRADES.getAegisShield);
            if (!ownedWeaponIds.includes('stasisField')) choices.push(ALL_UPGRADES.getStasisField);
        }
        const finalChoices = [...new Set(choices)].sort(() => 0.5 - Math.random()).slice(0, 3);
        setUpgradeChoices(finalChoices);
    };

    const createPlayer = () => {
        let soulPower = 1.0;
        let startingArmor = 0;
    
        // --- Soul Power Calculation ---
        if (isSpectatorMode) {
            // Spectator AI gets a much more powerful scaling
            const minSouls = 100;
            const maxSouls = 25000;
            const minBonus = 0.50; // 10%
            const maxBonus = 1.5; 
    
            let bonus = 0;
            if (souls >= minSouls) {
                const progress = (Math.min(souls, maxSouls) - minSouls) / (maxSouls - minSouls);
                const curvedProgress = Math.pow(progress, 0.75);
                const bonusRange = maxBonus - minBonus;
                bonus = minBonus + (curvedProgress * bonusRange);
            }
            soulPower = 1 + bonus;
            startingArmor = Math.floor(souls / 150);
        } else {
            // Player scaling is more modest
            const minSouls = 100;
            const maxSouls = 10000;
            const minBonus = 0.05; // 5%
            const maxBonus = 2.00; // 200%
    
            let bonus = 0;
            if (souls >= minSouls) {
                const progress = (Math.min(souls, maxSouls) - minSouls) / (maxSouls - minSouls);
                const curvedProgress = Math.pow(progress, 0.75);
                const bonusRange = maxBonus - minBonus;
                bonus = minBonus + (curvedProgress * bonusRange);
            }
            soulPower = 1 + bonus;
            startingArmor = Math.floor(souls / 500); // Players get less armor from souls
        }
    
        const startingHealth = PLAYER_INITIAL_HEALTH * soulPower;
    
        return {
            id: getNextId(), position: { x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 }, velocity: { x: 0, y: 0 },
            radius: PLAYER_RADIUS, health: startingHealth, maxHealth: startingHealth, isAlive: true,
            level: 1, xp: 0, xpToNextLevel: BASE_XP_TO_NEXT_LEVEL, overdrive: 0, maxOverdrive: PLAYER_MAX_OVERDRIVE,
            isOverdriveActive: false, overdriveTimer: 0, weapons: [createCelestialBeam()],
            movement: { up: false, down: false, left: false, right: false }, rotation: -Math.PI / 2,
            armor: startingArmor,
            soulPower: soulPower, // Store for dynamic speed/damage scaling
        };
    };

    const resetGame = useCallback(() => {
        playerRef.current = createPlayer();
        enemiesRef.current = []; projectilesRef.current = []; particlesRef.current = []; explosionsRef.current = [];
        vortexesRef.current = []; dronesRef.current = []; beamsRef.current = [];
        setScore(0); setGameTime(0); setCombo(0); setStyleRank('D');
        setNextBossTime(BOSS_SPAWN_TIME);
        nextBossTimeRef.current = BOSS_SPAWN_TIME;
        enemySpawnTimerRef.current = ENEMY_SPAWN_RATE_INITIAL;
        currentSpawnRateRef.current = ENEMY_SPAWN_RATE_INITIAL;
        lastTimeRef.current = performance.now();
    }, [souls, isSpectatorMode]);

    const startGame = () => {
        resetGame();
        setGameState(GameState.Playing);
    };

    const handleGameOver = useCallback(() => {
        setGameState(GameState.GameOver);
        if (!isSpectatorMode) {
            if (onGameSubmit) {
                onGameSubmit('starlightSentinel', scoreRef.current, []); 
            }
            if (onGameOver) {
                onGameOver();
            }
        }
        cancelAnimationFrame(gameLoopId.current);
    }, [onGameSubmit, onGameOver, isSpectatorMode]);

    const applyUpgrade = (upgrade) => {
        if (playerRef.current) {
            upgrade.apply(playerRef.current);
            setGameState(GameState.Playing);
        }
    };

    const addXp = (amount) => {
        const player = playerRef.current;
        if (!player) return;

        let finalAmount = amount;
        if (isSpectatorMode) {
            const minSouls = 100;
            const maxSouls = 25000;
            const minMultiplier = 1.0;
            const maxMultiplier = 2.5;
            let xpMultiplier = 1.0;

            if (souls >= minSouls) {
                const progress = (Math.min(souls, maxSouls) - minSouls) / (maxSouls - minSouls);
                const curvedProgress = Math.pow(progress, 0.75);
                const multiplierRange = maxMultiplier - minMultiplier;
                xpMultiplier = minMultiplier + (curvedProgress * multiplierRange);
            }
            finalAmount = amount * xpMultiplier;
        }

        player.xp += finalAmount;
        
        if (player.xp >= player.xpToNextLevel) {
            player.level++;
            player.xp -= player.xpToNextLevel;

            // --- Soul Power Scaled Level Up Bonuses ---
            const armorGain = 1 + Math.floor(2 * player.soulPower); // Reduced armor gain
            const healthGain = 5 + Math.floor(10 * player.soulPower); // Reduced health gain

            player.armor += isSpectatorMode ? armorGain : 6;
            player.maxHealth += isSpectatorMode ? healthGain : 20;

            // Cap maxHealth at 999
            if (player.maxHealth > 999) {
                player.maxHealth = 999;
            }

            const multiplier = player.level < 30 ? XP_MULTIPLIER_EARLY : XP_MULTIPLIER_LATE;
            player.xpToNextLevel = Math.floor(player.xpToNextLevel * multiplier);
            player.health = player.maxHealth;
            generateUpgradeChoices();
            setGameState(GameState.LevelUp);
        }
    };

    const spawnEnemy = () => {
        const player = playerRef.current;
        if (!player) return;

        // Boss spawn logic
        if (gameTime >= nextBossTimeRef.current) {
            const bossCount = enemiesRef.current.filter(e => e.type === EnemyType.Boss).length;
            const bossHealth = ENEMY_STATS.Boss.health * (1 + bossCount * 0.5); // Scale health for each new boss

            enemiesRef.current.push({
                id: getNextId(),
                position: { x: GAME_WIDTH / 2, y: -100 },
                velocity: { x: 0, y: 1 },
                ...ENEMY_STATS.Boss,
                health: bossHealth,
                maxHealth: bossHealth,
                isAlive: true,
                type: EnemyType.Boss,
                aiState: 'entering', // Initial state
                aiTimer: 3000,       // Timer for the current state
                phaseTimer: 10000,   // Timer for attack phases
            });
            
            const newNextBossTime = gameTime + BOSS_SPAWN_TIME;
            nextBossTimeRef.current = newNextBossTime;
            setNextBossTime(newNextBossTime);
            return; 
        }

        // Determine which enemies can spawn based on game time
        const spawnPool = [EnemyType.Phantom];
        if (gameTime > 30000) spawnPool.push(EnemyType.Reaper);
        if (gameTime > 60000) spawnPool.push(EnemyType.Hunter);
        if (gameTime > 90000) spawnPool.push(EnemyType.Carrier);
        
        const type = spawnPool[Math.floor(Math.random() * spawnPool.length)];
        const stats = ENEMY_STATS[type];

        const side = Math.floor(Math.random() * 4);
        let pos;
        if (side === 0) pos = { x: -50, y: Math.random() * GAME_HEIGHT };
        else if (side === 1) pos = { x: GAME_WIDTH + 50, y: Math.random() * GAME_HEIGHT };
        else if (side === 2) pos = { x: Math.random() * GAME_WIDTH, y: -50 };
        else pos = { x: Math.random() * GAME_WIDTH, y: GAME_HEIGHT + 50 };
        
        const healthMultiplier = 1 + (gameTime / 1000) * ENEMY_HEALTH_SCALE_PER_SECOND;
        const isElite = Math.random() < 0.15 && type !== EnemyType.Carrier; // Carriers can't be elite
        const baseHealth = isElite ? stats.health * 3 : stats.health;
        const health = baseHealth * healthMultiplier;
        const xpValue = isElite ? stats.xp * 2.5 : stats.xp;
        const radius = isElite ? stats.radius * 1.25 : stats.radius;
        const damage = isElite ? stats.damage * 1.5 : stats.damage;

        enemiesRef.current.push({
            id: getNextId(), position: pos, velocity: { x: 0, y: 0 }, radius: radius, health: health,
            maxHealth: health, isAlive: true, type, isElite, xpValue, damage,
            aiTimer: 0, aiState: 'moving', fireCooldown: stats.fireRate || 0,
        });
    };

    const createParticles = (position, count, color) => {
        for (let i = 0; i < count; i++) {
            particlesRef.current.push({
                id: getNextId(), position: { ...position }, velocity: { x: getRandom(-3, 3), y: getRandom(-3, 3) },
                radius: getRandom(1, 3), color: color, lifespan: getRandom(300, 800), maxLifespan: 800,
            });
        }
    };

    const createExplosion = (position, maxRadius, color) => {
        explosionsRef.current.push({
            id: getNextId(), position: { ...position }, velocity: { x: 0, y: 0 }, radius: 0,
            currentRadius: 0, maxRadius, color, lifespan: 300, maxLifespan: 300,
        });
    };

    const gameLoop = useCallback((currentTime) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const deltaTime = currentTime - lastTimeRef.current;
        lastTimeRef.current = currentTime;

        if (gameState !== GameState.Playing) {
            gameLoopId.current = requestAnimationFrame(gameLoop);
            return;
        }

        const player = playerRef.current;
        if (!player || (!player.isAlive && !isRemoteMode)) {
            handleGameOver();
            return;
        }

        setGameTime(prev => prev + deltaTime);

        // --- SYNC WRITE (Active Player) ---
        if (!isSpectatorMode && !isRemoteMode && syncId && myRole) {
            sendPlayerState(syncId, myRole, {
                pos: player.position,
                rot: player.rotation,
                overdrive: player.isOverdriveActive
            });
        }

        // --- UPDATE ---
        if (!isRemoteMode) {
            const currentSpeed = PLAYER_SPEED * player.soulPower;
            player.velocity = { x: 0, y: 0 };
            if (player.movement.up) player.velocity.y = -currentSpeed;
            if (player.movement.down) player.velocity.y = currentSpeed;
            if (player.movement.left) player.velocity.x = -currentSpeed;
            if (player.movement.right) player.velocity.x = currentSpeed;

            if (player.lastTargetPosition) {
                const aimAngle = Math.atan2(player.lastTargetPosition.y - player.position.y, player.lastTargetPosition.x - player.position.x);
                player.rotation = aimAngle + Math.PI / 2;
            } else if (player.velocity.x !== 0 || player.velocity.y !== 0) {
                player.rotation = Math.atan2(player.velocity.y, player.velocity.x) + Math.PI / 2;
            }

            player.position.x = Math.max(player.radius, Math.min(GAME_WIDTH - player.radius, player.position.x + player.velocity.x));
            player.position.y = Math.max(player.radius, Math.min(GAME_HEIGHT - player.radius, player.position.y + player.velocity.y));
        }

        player.weapons.forEach(w => {
            w.timer -= deltaTime;
            w.update(player, enemiesRef.current, projectilesRef.current, vortexesRef.current, dronesRef.current, beamsRef.current, { width: GAME_WIDTH, height: GAME_HEIGHT });
        });

        if (player.isOverdriveActive) {
            player.overdriveTimer -= deltaTime;
            if (player.overdriveTimer <= 0) player.isOverdriveActive = false;
        }

        projectilesRef.current.forEach(p => {
            if (p.targetId) {
                const target = enemiesRef.current.find(e => e.id === p.targetId);
                if (target) {
                    const angleToTarget = Math.atan2(target.position.y - p.position.y, target.position.x - p.position.x);
                    const currentAngle = Math.atan2(p.velocity.y, p.velocity.x);
                    let angleDiff = angleToTarget - currentAngle;
                    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
                    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
                    const turnAmount = Math.min(Math.abs(angleDiff), p.turnRate ?? 0.1);
                    const newAngle = currentAngle + Math.sign(angleDiff) * turnAmount;
                    const speed = Math.sqrt(p.velocity.x ** 2 + p.velocity.y ** 2);
                    p.velocity.x = Math.cos(newAngle) * speed;
                    p.velocity.y = Math.sin(newAngle) * speed;
                }
            }
            p.position.x += p.velocity.x;
            p.position.y += p.velocity.y;
            p.lifespan -= deltaTime;
            if (p.lifespan <= 0) p.isAlive = false;
        });

        enemySpawnTimerRef.current -= deltaTime;
        if (enemySpawnTimerRef.current <= 0) {
            spawnEnemy();
            // Reset the timer with the current rate, accounting for any leftover time
            enemySpawnTimerRef.current += currentSpawnRateRef.current; 
            // Calculate the next spawn rate
            currentSpawnRateRef.current = Math.max(ENEMY_SPAWN_RATE_MIN, currentSpawnRateRef.current * ENEMY_SPAWN_ACCELERATION);
        }

        enemiesRef.current.forEach(e => {
            // Reset effects for this frame
            let speedMultiplier = 1.0;
            e.effects = e.effects || {};
            if (e.effects.stasisSlow) {
                speedMultiplier = e.effects.stasisSlow;
                e.effects.stasisSlow = undefined; // Effect is reapplied by weapon each frame
            }
            if (e.effects.frozen) {
                if (e.effects.freezeTimer > 0) {
                    e.effects.freezeTimer -= deltaTime;
                    speedMultiplier = 0;
                } else {
                    e.effects.frozen = false;
                    e.effects.freezeBuildup = 0;
                }
            }

            // Specific AI behaviors
            switch (e.type) {
                case EnemyType.Boss:
                    e.aiTimer -= deltaTime;
                    const angleToPlayer = Math.atan2(player.position.y - e.position.y, player.position.x - e.position.x);
                    const distanceToPlayer = dist(player.position, e.position);
                    
                    // --- Player Prediction ---
                    const playerSpeed = PLAYER_SPEED * player.soulPower;
                    const playerVelocity = { x: 0, y: 0 };
                    if (player.movement.up) playerVelocity.y = -playerSpeed;
                    if (player.movement.down) playerVelocity.y = playerSpeed;
                    if (player.movement.left) playerVelocity.x = -playerSpeed;
                    if (player.movement.right) playerVelocity.x = playerSpeed;
                    const predictionTime = 0.5; // How far ahead to predict
                    const predictedPlayerPos = {
                        x: player.position.x + playerVelocity.x * predictionTime * 15, // Multiplier to adjust prediction
                        y: player.position.y + playerVelocity.y * predictionTime * 15,
                    };
                    const angleToPredictedPos = Math.atan2(predictedPlayerPos.y - e.position.y, predictedPlayerPos.x - e.position.x);

                    // --- State Machine ---
                    if (e.aiState === 'entering') {
                        e.velocity.y = 1; // Slowly move down onto the screen
                        if (e.aiTimer <= 0) {
                            e.aiState = 'strafing';
                            e.phaseTimer = 5000; // Start first attack phase in 5s
                        }
                    } else { // Active combat states
                        e.phaseTimer -= deltaTime;
                        if (e.phaseTimer <= 0) {
                            const nextPhase = Math.random();
                            if (nextPhase < 0.4) {
                                e.aiState = 'laser_barrage';
                                e.aiTimer = 3000; 
                            } else if (nextPhase < 0.7) {
                                e.aiState = 'projectile_volley';
                                e.aiTimer = 4000;
                            } else {
                                e.aiState = 'spawn_minions';
                                e.aiTimer = 1000;
                            }
                            e.phaseTimer = 7000 + Math.random() * 3000; // Next phase in 7-10 seconds
                        }
                    }

                    // --- Movement Logic ---
                    if (e.aiState !== 'entering') {
                        let targetVelocity = { x: 0, y: 0 };
                        const optimalDistance = player.isOverdriveActive ? 450 : 300; // Stay further away if player is in Overdrive
                        const speed = player.isOverdriveActive ? ENEMY_STATS.Boss.speed * 1.5 : ENEMY_STATS.Boss.speed; // Be faster when defensive

                        // Strafe logic: switch direction every 1.5 seconds for unpredictability
                        if (!e.strafeDirection || e.aiTimer % 1500 < 16) {
                            e.strafeDirection = Math.random() < 0.5 ? 1 : -1;
                        }
                        const perpendicularAngle = angleToPlayer + (Math.PI / 2) * e.strafeDirection;
                        targetVelocity.x += Math.cos(perpendicularAngle) * speed;
                        targetVelocity.y += Math.sin(perpendicularAngle) * speed;

                        // Maintain optimal distance
                        if (distanceToPlayer > optimalDistance + 50) {
                            targetVelocity.x += Math.cos(angleToPlayer) * speed;
                            targetVelocity.y += Math.sin(angleToPlayer) * speed;
                        } else if (distanceToPlayer < optimalDistance - 50) {
                            targetVelocity.x -= Math.cos(angleToPlayer) * speed;
                            targetVelocity.y -= Math.sin(angleToPlayer) * speed;
                        }
                        
                        e.velocity.x += (targetVelocity.x - e.velocity.x) * 0.08; // More responsive movement
                        e.velocity.y += (targetVelocity.y - e.velocity.y) * 0.08;
                    }

                    // --- Attack Logic ---
                    if (e.aiTimer > 0 && !player.isOverdriveActive) { // Don't attack if player is in Overdrive
                        if (e.aiState === 'laser_barrage') {
                            if (e.aiTimer % 1000 < 16) { // Fire a laser every second
                                // One laser at current position, one at predicted
                                beamsRef.current.push({ id: getNextId(), start: { ...e.position }, end: player.position, width: 15, color: '#FF00FF', lifespan: 400, maxLifespan: 400 });
                                beamsRef.current.push({ id: getNextId(), start: { ...e.position }, end: predictedPlayerPos, width: 15, color: '#FF00FF', lifespan: 400, maxLifespan: 400 });
                                // Simple hitscan damage for lasers
                                if (dist(player.position, e.position) < GAME_WIDTH) player.health -= 15;
                            }
                        } else if (e.aiState === 'projectile_volley') {
                            if (e.aiTimer % 500 < 16) { // Fire a volley every 0.5 seconds
                                // Fire a cone of 5 projectiles towards the predicted position
                                for (let i = -2; i <= 2; i++) {
                                    const volleyAngle = angleToPredictedPos + (i * 0.15); // 0.15 radian spread
                                    projectilesRef.current.push(createBossProjectile(e, volleyAngle));
                                }
                            }
                        } else if (e.aiState === 'spawn_minions' && e.aiTimer > 800) {
                             for (let i = 0; i < 2; i++) {
                                enemiesRef.current.push({ id: getNextId(), position: { ...e.position }, velocity: { x: getRandom(-2, 2), y: getRandom(-2, 2) }, ...ENEMY_STATS.Phantom, isAlive: true, type: EnemyType.Phantom });
                             }
                             if (gameTime > BOSS_SPAWN_TIME * 2) { // Spawn Reapers after 2nd boss timer
                                enemiesRef.current.push({ id: getNextId(), position: { ...e.position }, velocity: { x: getRandom(-2, 2), y: getRandom(-2, 2) }, ...ENEMY_STATS.Reaper, isAlive: true, type: EnemyType.Reaper, fireCooldown: 3000 });
                             }
                             e.aiTimer = 100; // Prevent re-spawning
                        }
                    }
                    break;
                case EnemyType.Reaper:
                    e.fireCooldown -= deltaTime;
                    if (e.fireCooldown <= 0) {
                        const angle = Math.atan2(player.position.y - e.position.y, player.position.x - e.position.x);
                        projectilesRef.current.push(createScytheProjectile(e, angle));
                        e.fireCooldown = ENEMY_STATS.Reaper.fireRate;
                    }
                    break;
                case EnemyType.Hunter:
                    e.aiTimer -= deltaTime;
                    if (e.aiState === 'moving') {
                        if (e.aiTimer <= 0) {
                            e.aiState = 'charging_dash';
                            e.aiTimer = 700; // Charge time
                            e.velocity = { x: 0, y: 0 };
                        }
                    } else if (e.aiState === 'charging_dash') {
                        if (e.aiTimer <= 0) {
                            e.aiState = 'dashing';
                            e.aiTimer = 250; // Dash duration
                            const behindPlayerAngle = Math.atan2(player.position.y - e.position.y, player.position.x - e.position.x) + Math.PI;
                            const dashSpeed = ENEMY_STATS.Hunter.speed * 3;
                            e.velocity = { x: Math.cos(behindPlayerAngle) * dashSpeed, y: Math.sin(behindPlayerAngle) * dashSpeed };
                        }
                    } else if (e.aiState === 'dashing') {
                        if (e.aiTimer <= 0) {
                            e.aiState = 'moving';
                            e.aiTimer = 3000; // Cooldown
                        }
                    }
                    break;
                default: // Phantom, Carrier, Mite, Boss
                    break;
            }

            // General movement
            if (e.aiState !== 'dashing' && e.aiState !== 'charging_dash') {
                const angle = Math.atan2(player.position.y - e.position.y, player.position.x - e.position.x);
                const speed = ENEMY_STATS[e.type].speed * (e.isElite ? 1.2 : 1) * speedMultiplier;
                e.velocity = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
            }
            e.position.x += e.velocity.x;
            e.position.y += e.velocity.y;
        });

        vortexesRef.current.forEach(v => {
            v.lifespan -= deltaTime;
            if (v.lifespan <= 0) {
                v.isAlive = false;
                if (player.weapons.find(w => w.id === 'starlightVortex')?.isEvolved) {
                    createExplosion(v.position, v.pullRadius, '#FF00FF');
                    enemiesRef.current.forEach(e => { if (dist(v.position, e.position) < v.pullRadius) e.health -= 150; });
                }
            }
            enemiesRef.current.forEach(e => {
                const distance = dist(v.position, e.position);
                if (distance < v.pullRadius) {
                    const angle = Math.atan2(v.position.y - e.position.y, v.position.x - e.position.x);
                    e.position.x += Math.cos(angle) * v.pullStrength * (1 - distance / v.pullRadius) * 20;
                    e.position.y += Math.sin(angle) * v.pullStrength * (1 - distance / v.pullRadius) * 20;
                    if (distance < v.radius) {
                        e.health -= v.damagePerSecond * (deltaTime / 1000);
                    }
                }
            });
        });

        dronesRef.current.forEach(d => {
            if (d.ownerId !== player.id) { d.isAlive = false; return; }
            d.orbitAngle += d.orbitSpeed * (deltaTime / 1000);
            d.position.x = player.position.x + Math.cos(d.orbitAngle) * d.orbitRadius;
            d.position.y = player.position.y + Math.sin(d.orbitAngle) * d.orbitRadius;
            d.fireTimer -= deltaTime;
            if (d.fireTimer <= 0 && enemiesRef.current.length > 0) {
                const target = enemiesRef.current.filter(e => dist(d.position, e.position) < 250).sort((a, b) => dist(d.position, a.position) - dist(d.position, b.position))[0];
                if (target) {
                    const weapon = player.weapons.find(w => w.id === 'guardianDrones');
                    if (d.isEvolved && weapon) {
                        beamsRef.current.push({ id: getNextId(), start: { ...d.position }, end: { ...target.position }, width: 10, color: '#00FF00', lifespan: 100, maxLifespan: 100 });
                        enemiesRef.current.forEach(e => { if (dist(e.position, d.position) < 250) e.health -= (20 + weapon.level * 5); });
                    } else if (weapon) {
                        const angle = Math.atan2(target.position.y - d.position.y, target.position.x - d.position.x);
                        projectilesRef.current.push(createDroneProjectile(d, angle, weapon.level));
                    }
                    d.fireTimer = d.fireCooldown;
                }
            }
        });

        particlesRef.current.forEach(p => { p.position.x += p.velocity.x; p.position.y += p.velocity.y; p.lifespan -= deltaTime; });
        explosionsRef.current.forEach(e => { e.lifespan -= deltaTime; e.currentRadius = e.maxRadius * (1 - e.lifespan / e.maxLifespan); });
        beamsRef.current.forEach(b => b.lifespan -= deltaTime);

        if (combo > 0) { comboTimeoutRef.current -= deltaTime; if (comboTimeoutRef.current <= 0) { setCombo(0); setStyleRank('D'); } }

        // --- COLLISION DETECTION ---
        const shield = player.weapons.find(w => w.id === 'aegisShield');
        const shieldRadius = player.radius + 15;

        projectilesRef.current.forEach(p => {
            if (!p.isAlive) return;

            // Shield collision with enemy projectiles
            if (shield && !p.isPlayerProjectile && shield.shieldHealth > 0 && dist(player.position, p.position) < shieldRadius + p.radius) {
                shield.shieldHealth -= p.damage;
                shield.regenTimer = shield.regenDelay;
                if (shield.isEvolved) {
                    shield.absorbedDamage += p.damage;
                }
                p.isAlive = false;
                createParticles(p.position, 5, '#00BFFF');
                return;
            }

            // Player projectile collision
            if (p.isPlayerProjectile) {
                enemiesRef.current.forEach(e => {
                    if (!e.isAlive) return;
                    const collisionLogic = () => {
                        const isFrozen = e.effects?.frozen;
                        const damageDealt = p.damage * player.soulPower * (isFrozen ? 1.5 : 1); // Frozen enemies take more damage
                        e.health -= damageDealt;

                        if (p.alreadyHit) { // For AoE attacks like Pulse Nova
                            if (!p.alreadyHit.includes(e.id)) p.alreadyHit.push(e.id);
                        } else {
                            p.isAlive = false;
                        }
                        
                        createParticles(p.position, 3, '#00FFFF');

                        if (e.health <= 0) {
                            e.isAlive = false;

                            // Carrier death spawn
                            if (e.type === EnemyType.Carrier) {
                                for (let i = 0; i < 8; i++) {
                                    enemiesRef.current.push({
                                        id: getNextId(),
                                        position: { ...e.position },
                                        velocity: { x: getRandom(-3, 3), y: getRandom(-3, 3) },
                                        ...ENEMY_STATS.Mite,
                                        isAlive: true,
                                        type: EnemyType.Mite,
                                    });
                                }
                            }
                            
                            // Temporal Singularity shatter effect
                            const stasisField = player.weapons.find(w => w.id === 'stasisField');
                            if (isFrozen && stasisField?.isEvolved) {
                                createExplosion(e.position, 80, '#00FFFF');
                                enemiesRef.current.forEach(otherEnemy => {
                                    if (otherEnemy.isAlive && dist(e.position, otherEnemy.position) < 80) {
                                        otherEnemy.health -= 50 + stasisField.level * 10;
                                    }
                                });
                            }

                            // Create XP Gem on enemy death
                            xpGemsRef.current.push({
                                id: getNextId(),
                                pos: { ...e.position },
                                value: e.xpValue,
                                radius: 3 + Math.log1p(e.xpValue), // Size based on value
                                lifespan: 8000, // 8 seconds to pick up
                                velocity: { x: getRandom(-1, 1), y: getRandom(-1, 1) }
                            });

                            const scoreGained = e.xpValue * (1 + Object.keys(STYLE_THRESHOLDS).indexOf(styleRank) * 0.2);
                            if (!isNaN(scoreGained)) {
                                setScore(s => s + scoreGained);
                            }
                            
                            const oldOverdrive = player.overdrive;
                            player.overdrive = Math.min(player.maxOverdrive, player.overdrive + 5);
                            if (isSpectatorMode && oldOverdrive < player.maxOverdrive && player.overdrive >= player.maxOverdrive) {
                                player.overdriveReadyTimestamp = gameTime;
                            }

                            createExplosion(e.position, e.radius * 2, e.isElite ? '#FF00FF' : '#FF4500');
                            createParticles(e.position, e.isElite ? 50 : 20, e.isElite ? '#FF00FF' : '#FFA500');
                            screenShakeRef.current = { magnitude: e.isElite ? 10 : 4, duration: 150 };
                            setCombo(c => { const newCombo = c + 1; if (newCombo >= STYLE_THRESHOLDS.SS) setStyleRank('SS'); else if (newCombo >= STYLE_THRESHOLDS.S) setStyleRank('S'); else if (newCombo >= STYLE_THRESHOLDS.A) setStyleRank('A'); else if (newCombo >= STYLE_THRESHOLDS.B) setStyleRank('B'); else if (newCombo >= STYLE_THRESHOLDS.C) setStyleRank('C'); return newCombo; });
                            comboTimeoutRef.current = COMBO_TIMEOUT;
                        }
                    }
                    if (p.alreadyHit && !p.alreadyHit.includes(e.id) && dist(p.position, e.position) < p.radius + e.radius) {
                        collisionLogic();
                    } else if (!p.alreadyHit && dist(p.position, e.position) < p.radius + e.radius) {
                        collisionLogic();
                    }
                });
            } 
            // Enemy projectile collision
            else {
                if (dist(player.position, p.position) < player.radius + p.radius) {
                    if (!isRemoteMode) {
                        const damageReduction = player.armor / (player.armor + 100);
                        const damageTaken = p.damage * (1 - damageReduction);
                        player.health -= damageTaken;
                        p.health--; 
                        if (p.health <= 0) p.isAlive = false;
                        screenShakeRef.current = { magnitude: 8, duration: 200 };
                        if (player.health <= 0) player.isAlive = false;
                    }
                }
            }
        });

        const XP_GEM_MAGNET_RADIUS = 250;
        const XP_GEM_MAGNET_SPEED = 8;

        xpGemsRef.current.forEach(gem => {
            gem.lifespan -= deltaTime;
            
            const distanceToPlayer = dist(player.position, gem.pos);

            if (distanceToPlayer < XP_GEM_MAGNET_RADIUS) {
                // Move towards player
                const angle = Math.atan2(player.position.y - gem.pos.y, player.position.x - gem.pos.x);
                gem.velocity.x = Math.cos(angle) * XP_GEM_MAGNET_SPEED;
                gem.velocity.y = Math.sin(angle) * XP_GEM_MAGNET_SPEED;
            } else {
                // Friction
                gem.velocity.x *= 0.98;
                gem.velocity.y *= 0.98;
            }
            
            gem.pos.x += gem.velocity.x;
            gem.pos.y += gem.velocity.y;

            if (distanceToPlayer < player.radius + gem.radius) { // Actual collision
                addXp(gem.value);
                gem.lifespan = 0; // Mark for cleanup
                createParticles(gem.pos, 5, '#00FF00');
            }
        });

        enemiesRef.current.forEach(e => {
            if (!e.isAlive) return;

            // Shield collision with enemies
            if (shield && shield.shieldHealth > 0 && dist(player.position, e.position) < shieldRadius + e.radius) {
                const shieldDamage = 10 + shield.level * 2;
                e.health -= shieldDamage;
                shield.shieldHealth -= e.damage;
                shield.regenTimer = shield.regenDelay;
                e.isAlive = false; // Enemy is destroyed on shield contact
                createExplosion(e.position, e.radius * 1.5, '#00BFFF');
                return;
            }

            if (dist(player.position, e.position) < player.radius + e.radius) {
                let damageTaken;
                if (e.type === EnemyType.Boss) {
                    damageTaken = player.health * 0.9;
                } else {
                    const damageReduction = player.armor / (player.armor + 100);
                    damageTaken = e.damage * (1 - damageReduction);
                }
                
                if (!isRemoteMode) {
                    player.health -= damageTaken;
                    if (player.health <= 0) player.isAlive = false;
                }
                
                if (e.type !== EnemyType.Boss) {
                    e.isAlive = false; 
                }
                
                createExplosion(e.position, e.radius * 1.5, '#FF0000');
                screenShakeRef.current = { magnitude: 8, duration: 200 };
            }
        });

        // --- CLEANUP ---
        projectilesRef.current = projectilesRef.current.filter(p => p.isAlive);
        enemiesRef.current = enemiesRef.current.filter(e => e.isAlive);
        particlesRef.current = particlesRef.current.filter(p => p.lifespan > 0);
        explosionsRef.current = explosionsRef.current.filter(e => e.lifespan > 0);
        vortexesRef.current = vortexesRef.current.filter(v => v.isAlive);
        dronesRef.current = dronesRef.current.filter(d => d.isAlive);
        beamsRef.current = beamsRef.current.filter(b => b.lifespan > 0);
        xpGemsRef.current = xpGemsRef.current.filter(g => g.lifespan > 0);

        // --- DRAW ---
        ctx.save();
        if (screenShakeRef.current.duration > 0) { const { magnitude } = screenShakeRef.current; ctx.translate((Math.random() - 0.5) * magnitude, (Math.random() - 0.5) * magnitude); screenShakeRef.current.duration -= deltaTime; } else { screenShakeRef.current = { magnitude: 0, duration: 0 }; }
        ctx.fillStyle = '#0a0a1a'; ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
        ctx.fillStyle = 'white'; const gameTimeSeconds = gameTime / 1000; for (let i = 0; i < 100; i++) { const x = (Math.sin(i * 1.23 + gameTimeSeconds * 0.01) * GAME_WIDTH / 2 + GAME_WIDTH / 2) % GAME_WIDTH; const y = (Math.cos(i * 3.45 + gameTimeSeconds * 0.01) * GAME_HEIGHT / 2 + GAME_HEIGHT / 2) % GAME_HEIGHT; const r = Math.sin(i * 0.5 + gameTimeSeconds * 0.1) * 0.5 + 0.5; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
        
        // Draw XP Gems
        xpGemsRef.current.forEach(gem => {
            ctx.fillStyle = '#00FF00';
            ctx.shadowColor = '#00FF00';
            ctx.shadowBlur = 10;
            ctx.beginPath();
            ctx.arc(gem.pos.x, gem.pos.y, gem.radius, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.shadowBlur = 0;

        // Draw Stasis Field
        const stasisField = player.weapons.find(w => w.id === 'stasisField');
        if (stasisField) {
            ctx.fillStyle = `rgba(0, 191, 255, ${0.1 + Math.sin(gameTime * 0.002) * 0.05})`;
            ctx.beginPath();
            ctx.arc(player.position.x, player.position.y, stasisField.radius, 0, Math.PI * 2);
            ctx.fill();
        }

        vortexesRef.current.forEach(v => { ctx.save(); ctx.translate(v.position.x, v.position.y); ctx.rotate(gameTime * 0.001); const alpha = v.lifespan / v.maxLifespan; for (let i = 0; i < 6; i++) { ctx.beginPath(); ctx.moveTo(0, 0); const angle = (i / 6) * Math.PI * 2; ctx.quadraticCurveTo(Math.cos(angle + 1) * v.pullRadius * 0.7, Math.sin(angle + 1) * v.pullRadius * 0.7, Math.cos(angle) * v.pullRadius, Math.sin(angle) * v.pullRadius); ctx.strokeStyle = `rgba(148, 0, 211, ${0.5 * alpha})`; ctx.lineWidth = 3; ctx.stroke(); } ctx.fillStyle = `rgba(0, 0, 0, ${0.7 * alpha})`; ctx.beginPath(); ctx.arc(0, 0, v.radius, 0, Math.PI * 2); ctx.fill(); ctx.restore(); });
        dronesRef.current.forEach(d => { ctx.fillStyle = d.isEvolved ? '#00FF00' : '#90ee90'; ctx.strokeStyle = d.isEvolved ? '#FFFFFF' : '#000000'; ctx.lineWidth = 2; ctx.beginPath(); if (d.isEvolved) { ctx.rect(d.position.x - d.radius, d.position.y - d.radius, d.radius * 2, d.radius * 2); } else { ctx.arc(d.position.x, d.position.y, d.radius, 0, Math.PI * 2); } ctx.fill(); ctx.stroke(); });
        
        // Draw Player and Shield
        if (playerImage) {
            ctx.save();
            ctx.translate(player.position.x, player.position.y);
            ctx.rotate(player.rotation);
            // Draw flame trail if moving
            if ((player.velocity.x !== 0 || player.velocity.y !== 0) && player.isAlive) {
                ctx.fillStyle = player.isOverdriveActive ? '#FFFF00' : '#FFA500';
                ctx.shadowColor = '#FFD700';
                ctx.shadowBlur = 15;
                const flameSize = player.radius * (1.2 + Math.random() * 0.5);
                ctx.beginPath();
                ctx.moveTo(0, player.radius * 0.8);
                ctx.lineTo(-player.radius * 0.6, player.radius * 0.8 + flameSize * 0.6);
                ctx.lineTo(0, player.radius * 0.8 + flameSize);
                ctx.lineTo(player.radius * 0.6, player.radius * 0.8 + flameSize * 0.6);
                ctx.closePath();
                ctx.fill();
            }
            ctx.drawImage(playerImage, -player.radius, -player.radius, player.radius * 2, player.radius * 2);
            ctx.restore();
        }
        /*
        ctx.save(); ctx.translate(player.position.x, player.position.y); ctx.rotate(player.rotation); ctx.fillStyle = player.isOverdriveActive ? '#FF00FF' : '#00FFFF'; ctx.shadowColor = player.isOverdriveActive ? '#FF00FF' : '#00FFFF'; ctx.shadowBlur = 20; ctx.beginPath(); ctx.moveTo(0, -player.radius); ctx.lineTo(-player.radius * 0.8, player.radius * 0.6); ctx.lineTo(player.radius * 0.8, player.radius * 0.6); ctx.closePath(); ctx.fill(); if ((player.velocity.x !== 0 || player.velocity.y !== 0) && player.isAlive) { ctx.fillStyle = player.isOverdriveActive ? '#FFFF00' : '#FFA500'; ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 15; const flameSize = player.radius * (0.8 + Math.random() * 0.4); ctx.beginPath(); ctx.moveTo(0, player.radius * 0.6); ctx.lineTo(-player.radius * 0.5, player.radius * 0.6 + flameSize * 0.5); ctx.lineTo(0, player.radius * 0.6 + flameSize); ctx.lineTo(player.radius * 0.5, player.radius * 0.6 + flameSize * 0.5); ctx.closePath(); ctx.fill(); } ctx.shadowBlur = 0; ctx.restore();
        */
        
        if (shield && shield.shieldHealth > 0) {
            ctx.strokeStyle = `rgba(0, 191, 255, ${0.5 + (shield.shieldHealth / shield.maxShieldHealth) * 0.5})`;
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(player.position.x, player.position.y, shieldRadius, 0, Math.PI * 2);
            ctx.stroke();
        }

        projectilesRef.current.forEach(p => p.render(ctx, p)); ctx.shadowBlur = 0;
        enemiesRef.current.forEach(e => {
            const enemyImage = e.type === EnemyType.Phantom ? phantomImage : e.type === EnemyType.Reaper ? reaperImage : null;
            if (enemyImage) {
                ctx.save();
                ctx.translate(e.position.x, e.position.y);
                ctx.drawImage(enemyImage, -e.radius, -e.radius, e.radius * 2, e.radius * 2);
                ctx.restore();
            } else {
                // Fallback for enemies without icons yet
                const originalColor = e.isElite ? '#9400D3' : '#FF4136';
                const frozenColor = '#ADD8E6';
                let currentColor = originalColor;
                if (e.effects?.frozen) {
                    currentColor = frozenColor;
                } else if (e.effects?.stasisSlow) {
                    const r1 = parseInt(originalColor.slice(1,3), 16); const g1 = parseInt(originalColor.slice(3,5), 16); const b1 = parseInt(originalColor.slice(5,7), 16);
                    const r2 = parseInt(frozenColor.slice(1,3), 16); const g2 = parseInt(frozenColor.slice(3,5), 16); const b2 = parseInt(frozenColor.slice(5,7), 16);
                    const p = 0.5;
                    const r = Math.round(r1 * (1-p) + r2 * p); const g = Math.round(g1 * (1-p) + g2 * p); const b = Math.round(b1 * (1-p) + b2 * p);
                    currentColor = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
                }

                if (e.isElite) { ctx.save(); ctx.shadowColor = '#FF00FF'; ctx.shadowBlur = 15 + Math.sin(gameTime * 0.005) * 10; ctx.fillStyle = currentColor; ctx.strokeStyle = '#FF00FF'; ctx.lineWidth = 3; if (e.type === EnemyType.Phantom && e.aiState === 'charging_dash') { const chargeRatio = 1 - (e.aiTimer / 500); ctx.fillStyle = `rgba(255, 0, 255, ${0.5 + chargeRatio * 0.5})`; ctx.beginPath(); ctx.arc(e.position.x, e.position.y, e.radius * (1 + chargeRatio * 0.5), 0, Math.PI * 2); ctx.fill(); } ctx.beginPath(); ctx.arc(e.position.x, e.position.y, e.radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); ctx.restore(); } else { ctx.fillStyle = currentColor; ctx.strokeStyle = '#FFC400'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(e.position.x, e.position.y, e.radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
            }
        }); ctx.shadowBlur = 0;
        particlesRef.current.forEach(p => { ctx.globalAlpha = Math.max(0, p.lifespan / p.maxLifespan); ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.position.x, p.position.y, p.radius, 0, Math.PI * 2); ctx.fill(); }); ctx.globalAlpha = 1.0;
        explosionsRef.current.forEach(e => { ctx.globalAlpha = Math.max(0, e.lifespan / e.maxLifespan); ctx.strokeStyle = e.color; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(e.position.x, e.position.y, Math.max(0, e.currentRadius), 0, Math.PI * 2); ctx.stroke(); }); ctx.globalAlpha = 1.0;
        beamsRef.current.forEach(b => { ctx.globalAlpha = Math.max(0, b.lifespan / b.maxLifespan); ctx.strokeStyle = b.color; ctx.lineWidth = b.width; ctx.shadowColor = b.color; ctx.shadowBlur = 15; ctx.beginPath(); ctx.moveTo(b.start.x, b.start.y); ctx.lineTo(b.end.x, b.end.y); ctx.stroke(); }); ctx.globalAlpha = 1.0; ctx.shadowBlur = 0;
        ctx.restore();
        gameLoopId.current = requestAnimationFrame(gameLoop);
    }, [gameState, handleGameOver, souls, isSpectatorMode]);

    useEffect(() => {
        if (gameState === GameState.Playing) {
            lastTimeRef.current = performance.now();
            gameLoopId.current = requestAnimationFrame(gameLoop);
        } else {
            cancelAnimationFrame(gameLoopId.current);
        }
        return () => cancelAnimationFrame(gameLoopId.current);
    }, [gameState, gameLoop]);

    useEffect(() => {
        if (!isSpectatorMode || gameState !== GameState.Playing) return;
        const aiInterval = setInterval(async () => {
            const player = playerRef.current;
            if (isAiThinking || !player) return;
            setIsAiThinking(true);
            const currentGameState = {
                player: { 
                    pos: player.position, 
                    health: player.health,
                    maxHealth: player.maxHealth, 
                    isOverdriveReady: player.overdrive >= player.maxOverdrive,
                    overdriveReadyTimestamp: player.overdriveReadyTimestamp,
                    weapons: player.weapons.map(w => ({ id: w.id, level: w.level, isEvolved: w.isEvolved }))
                },
                enemies: enemiesRef.current.map(e => ({ pos: e.position, type: e.type, velocity: e.velocity, isElite: e.isElite, aiState: e.aiState })),
                projectiles: projectilesRef.current.map(p => ({ pos: p.position, velocity: p.velocity, isPlayerProjectile: p.isPlayerProjectile })),
                xpGems: xpGemsRef.current.map(g => ({ pos: g.pos })),
                gameBounds: { width: GAME_WIDTH, height: GAME_HEIGHT },
                gameTime: gameTime,
            };
            try {
                const getAiMoveCloudFn = httpsCallable(appFunctions, 'getAiGameMove');
                const result = await getAiMoveCloudFn({ gameType: 'starlightSentinel', dreamWeaveId, souls, gameState: currentGameState });
                const { action, overdrive } = result.data;
                
                lastAiActionTimestamp.current = gameTime;

                if (action !== null) {
                    player.movement = {
                        up: action === 0,
                        down: action === 1,
                        left: action === 2,
                        right: action === 3,
                    };
                }

                if (overdrive && player.overdrive >= player.maxOverdrive) {
                    player.overdrive = 0;
                    player.isOverdriveActive = true;
                    player.overdriveTimer = OVERDRIVE_DURATION;
                    player.overdriveReadyTimestamp = null;
                    screenShakeRef.current = { magnitude: 15, duration: 500 };
                }
            } catch (error) {
                console.error("Error fetching AI move for Starlight Sentinel:", error);
                const randomMove = Math.floor(Math.random() * 4);
                 player.movement = {
                    up: randomMove === 0,
                    down: randomMove === 1,
                    left: randomMove === 2,
                    right: randomMove === 3,
                };
            } finally {
                setIsAiThinking(false);
            }
        }, AI_THINK_INTERVAL);
        return () => clearInterval(aiInterval);
    }, [isSpectatorMode, gameState, isAiThinking, appFunctions, dreamWeaveId, souls]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isSpectatorMode || isRemoteMode) return;
            const player = playerRef.current; if (!player) return;
            if (e.key === 'w' || e.key === 'ArrowUp') player.movement.up = true;
            if (e.key === 's' || e.key === 'ArrowDown') player.movement.down = true;
            if (e.key === 'a' || e.key === 'ArrowLeft') player.movement.left = true;
            if (e.key === 'd' || e.key === 'ArrowRight') player.movement.right = true;
            if (e.key === ' ' && player.overdrive >= player.maxOverdrive) {
                player.overdrive = 0; player.isOverdriveActive = true; player.overdriveTimer = OVERDRIVE_DURATION;
                screenShakeRef.current = { magnitude: 15, duration: 500 };
            }
        };
        const handleKeyUp = (e) => {
            if (isSpectatorMode || isRemoteMode) return;
            const player = playerRef.current; if (!player) return;
            if (e.key === 'w' || e.key === 'ArrowUp') player.movement.up = false;
            if (e.key === 's' || e.key === 'ArrowDown') player.movement.down = false;
            if (e.key === 'a' || e.key === 'ArrowLeft') player.movement.left = false;
            if (e.key === 'd' || e.key === 'ArrowRight') player.movement.right = false;
        };
        window.addEventListener('keydown', handleKeyDown); window.addEventListener('keyup', handleKeyUp);
        return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('keyup', handleKeyUp); };
    }, [isSpectatorMode, isRemoteMode]);

    useEffect(() => {
        if (gameState === GameState.LevelUp && isSpectatorMode) {
            const aiChooseUpgrade = () => {
                const player = playerRef.current;
                if (!player || !upgradeChoices || upgradeChoices.length === 0) {
                    setGameState(GameState.Playing);
                    return;
                }

                // --- High-Soul Override for Celestial Beam ---
                const HIGH_SOUL_THRESHOLD = 15000;
                if (souls >= HIGH_SOUL_THRESHOLD) {
                    const celestialBeamUpgrade = upgradeChoices.find(u => u.id === 'celestialBeam_upgrade' || u.id === 'trinityBeam');
                    if (celestialBeamUpgrade) {
                        applyUpgrade(celestialBeamUpgrade);
                        return; // Prioritize and exit
                    }
                }

                // --- 1. Define Weapon Archetypes and Synergies ---
                const WEAPON_ARCHETYPES = {
                    pulseNova: 'Area of Effect',
                    starlightVortex: 'Crowd Control',
                    guardianDrones: 'Defense',
                    starfireMissiles: 'Single-Target',
                    warpStrike: 'Utility',
                    aegisShield: 'Defense',
                    stasisField: 'Crowd Control',
                    celestialBeam: 'Single-Target',
                };

                // --- 2. Determine AI "Personality" based on Souls ---
                const MAX_SOULS_FOR_STRATEGY = 20000;
                const skill = Math.min(1, souls / MAX_SOULS_FOR_STRATEGY);
                let targetArchetype;

                if (skill < 0.3) {
                    targetArchetype = 'Area of Effect'; // Low-soul AI focuses on clearing weak enemies.
                } else if (skill < 0.7) {
                    targetArchetype = 'Balanced'; // Mid-soul AI builds a mix of offense and defense.
                } else {
                    targetArchetype = 'Crowd Control'; // High-soul AI aims for strategic, high-synergy builds.
                }

                // --- 3. Score Available Upgrades ---
                const scoreUpgrade = (upgrade) => {
                    let score = 0;
                    const ownedWeaponIds = player.weapons.map(w => w.id);

                    // a. Evolution is always the highest priority.
                    if (upgrade.type === UpgradeType.WeaponEvolution) {
                        return 1000;
                    }

                    const weaponId = upgrade.id.startsWith('get')
                        ? upgrade.id.replace('get', '').charAt(0).toLowerCase() + upgrade.id.slice(4)
                        : upgrade.id.replace('_upgrade', '');

                    const archetype = WEAPON_ARCHETYPES[weaponId];

                    // b. Score based on archetype preference.
                    if (targetArchetype === 'Balanced') {
                        score += 50; // General bonus for any upgrade.
                        // Encourage diversity in balanced builds.
                        if (ownedWeaponIds.includes(weaponId)) {
                            score += 20;
                        } else {
                            score += 30;
                        }
                    } else if (archetype === targetArchetype) {
                        score += 100; // High score for matching the target archetype.
                    }

                    // c. Synergies with existing weapons.
                    if (ownedWeaponIds.includes('stasisField') && archetype === 'Single-Target') {
                        score += 50; // Stasis Field + Single-Target is a powerful combo.
                    }
                    if (ownedWeaponIds.includes('starlightVortex') && archetype === 'Area of Effect') {
                        score += 40; // Vortex + AoE enhances crowd clearing.
                    }

                    // d. Prioritize upgrading existing weapons over adding new ones, especially later on.
                    if (upgrade.id.includes('_upgrade')) {
                        score += 25 + player.level * 2;
                    } else {
                        // Lower the score for new weapons if the inventory is almost full.
                        if (player.weapons.length >= 4) {
                            score -= 20;
                        }
                    }
                    
                    // e. Add a small random factor to prevent identical builds every time.
                    score += Math.random() * 10;

                    return score;
                };

                // --- 4. Select the Best Upgrade ---
                const scoredChoices = upgradeChoices.map(upgrade => ({
                    upgrade,
                    score: scoreUpgrade(upgrade),
                })).sort((a, b) => b.score - a.score);

                const bestChoice = scoredChoices[0]?.upgrade || upgradeChoices[0];

                applyUpgrade(bestChoice);
            };

            // Simulate "thinking" time for the AI.
            const thinkTimer = setTimeout(aiChooseUpgrade, 1500);
            return () => clearTimeout(thinkTimer);
        }
    }, [gameState, isSpectatorMode, upgradeChoices, souls]);

    const player = playerRef.current;

    return (
        <div className="w-full h-full flex items-center justify-center bg-black p-4">
            <div className="relative w-full max-w-6xl aspect-[16/9]">
                <canvas ref={canvasRef} width={GAME_WIDTH} height={GAME_HEIGHT} className="w-full h-full" />
                {gameState === GameState.MainMenu && (
                    <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center text-white z-10">
                        <h1 className="text-4xl md:text-6xl font-bold text-cyan-300 mb-4 tracking-widest" style={{ textShadow: '0 0 10px #0ff, 0 0 20px #0ff' }}>{t('starlightSentinel.mainMenu.title')}</h1>
                        <p className="text-lg md:text-xl text-purple-300 mb-8">{t('starlightSentinel.mainMenu.subtitle')}</p>
                        {!isSpectatorMode && !isRemoteMode ? (
                            <>
                                <button onClick={startGame} className="px-8 py-4 bg-purple-600 text-white font-bold text-xl md:text-2xl rounded-lg hover:bg-purple-500 transition-all duration-300 shadow-lg shadow-purple-500/50">{t('starlightSentinel.mainMenu.engage')}</button>
                                <div className="mt-8 text-center text-gray-400 text-sm md:text-base"><p>{t('starlightSentinel.mainMenu.controlsTitle')}</p><p>{t('starlightSentinel.mainMenu.controlsMove')}</p><p>{t('starlightSentinel.mainMenu.controlsOverdrive')}</p></div>
                            </>
                        ) : (
                            <button onClick={startGame} className="px-8 py-4 bg-indigo-600 text-white font-bold text-xl md:text-2xl rounded-lg hover:bg-indigo-500 transition-all duration-300 shadow-lg shadow-indigo-500/50">
                                {isRemoteMode ? "Watch Live Feed" : t('starlightSentinel.mainMenu.startSpectator')}
                            </button>
                        )}
                    </div>
                )}
                {(gameState === GameState.Playing && player) && (
                    <div className="absolute inset-0 p-2 md:p-4 text-white pointer-events-none z-5">
                        <div className="flex justify-between items-start">
                            <div className="text-left">
                                <div className="text-xl md:text-3xl font-bold text-cyan-300" style={{ textShadow: '0 0 5px #0ff' }}>{t('starlightSentinel.hud.score')}: {Math.floor(score)}</div>
                                <div className="text-lg md:text-2xl font-bold text-purple-300" style={{ textShadow: '0 0 5px #f0f' }}>{t('starlightSentinel.hud.bossIn')}: {nextBossTime - gameTime > 0 ? new Date(nextBossTime - gameTime).toISOString().slice(14, 19) : t('starlightSentinel.hud.bossNow')}</div>
                                <div className="text-base md:text-lg font-bold text-yellow-300" style={{ textShadow: '0 0 5px #ff0' }}>{t('starlightSentinel.hud.soulPower')}: {player.soulPower.toFixed(2)}x</div>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center justify-end">
                                    <span className={`text-3xl md:text-5xl font-black transition-all duration-200 ${styleRank === 'S' ? 'text-orange-400' : styleRank === 'SS' ? 'text-red-500' : 'text-purple-400'}`}>{styleRank}</span>
                                    <span className="text-xl md:text-3xl font-bold ml-2 md:ml-4">x{combo}</span>
                                </div>
                            </div>
                        </div>
                        <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4">
                            <div className="w-full bg-gray-700 rounded-full h-4 md:h-6 border-2 border-gray-500 mb-2">
                                <div className="bg-green-500 h-full rounded-full transition-all duration-200" style={{ width: `${(player.health / player.maxHealth) * 100}%` }}></div>
                                <span className="absolute w-full text-center top-0 text-xs md:text-sm font-bold">{Math.ceil(player.health)} / {player.maxHealth}</span>
                            </div>
                            <div className="flex justify-between text-xs md:text-sm">
                                <div className="w-full bg-gray-700 rounded-full h-3 md:h-4 border-2 border-gray-500 mr-1">
                                    <div className="bg-blue-400 h-full rounded-full transition-all duration-200" style={{ width: `${(player.xp / player.xpToNextLevel) * 100}%` }}></div>
                                    <span className="absolute w-full text-center top-5 md:top-6 text-xs font-bold">{t('starlightSentinel.hud.level')} {player.level}</span>
                                </div>
                                <div className="w-auto flex-shrink-0 text-right">
                                    <span className="font-bold text-gray-300">{t('starlightSentinel.hud.armor')}: {player.armor} ({(player.armor / (player.armor + 100) * 100).toFixed(1)}% {t('starlightSentinel.hud.damageReduction')})</span>
                                </div>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-3 md:h-4 border-2 border-purple-500 mt-2">
                                <div className="bg-purple-400 h-full rounded-full transition-all duration-200" style={{ width: `${(player.overdrive / player.maxOverdrive) * 100}%` }}></div>
                            </div>
                        </div>
                    </div>
                )}
                {gameState === GameState.LevelUp && (
                    <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-20 p-4">
                        <h2 className="text-3xl md:text-5xl text-yellow-300 font-bold mb-6 md:mb-8" style={{ textShadow: '0 0 10px #ff0' }}>{t('starlightSentinel.levelUp.title')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                            {upgradeChoices.map(upgrade => (
                                <div key={upgrade.id} onClick={() => applyUpgrade(upgrade)} className="bg-gray-800 border-2 border-cyan-400 p-4 md:p-6 rounded-lg cursor-pointer hover:bg-gray-700 hover:border-cyan-200 transition-all duration-200 text-center">
                                    <h3 className="text-lg md:text-2xl font-bold text-cyan-300 mb-2">{upgrade.name}</h3>
                                    <p className="text-sm md:text-base text-gray-300">{upgrade.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {gameState === GameState.GameOver && (
                    <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center text-white z-10 p-4">
                        <h1 className="text-4xl md:text-6xl font-bold text-red-500 mb-4 tracking-widest" style={{ textShadow: '0 0 10px #f00, 0 0 20px #f00' }}>{t('starlightSentinel.gameOver.title')}</h1>
                        <p className="text-2xl md:text-3xl text-gray-300 mb-8">{t('starlightSentinel.gameOver.finalScore')}: {Math.floor(score)}</p>
                        
                        <div className="mb-8 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-center">
                            <p className="text-sm text-red-200 uppercase tracking-widest font-bold mb-1">Raid Boss Damage</p>
                            <p className="text-3xl font-orbitron text-red-400">+{Math.floor(score * 5)}</p>
                        </div>

                        <button onClick={startGame} className="px-8 py-4 bg-cyan-600 text-white font-bold text-xl md:text-2xl rounded-lg hover:bg-cyan-500 transition-all duration-300 shadow-lg shadow-cyan-500/50">{t('starlightSentinel.gameOver.reEngage')}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StarlightSentinel;
